import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, CheckCircle2 } from 'lucide-react';
export function Slide05Hypothesis() {
  return <div className="w-full h-full flex flex-col justify-center px-[clamp(4rem,8vw,10rem)]" data-id="element-129">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-[clamp(4rem,8vw,8rem)] items-center" data-id="element-130">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} data-id="element-131">
          <span className="text-[var(--accent)] font-semibold tracking-widest uppercase text-sm mb-4 block" data-id="element-132">
            The Hypothesis
          </span>
          <h2 className="font-display text-[clamp(3rem,4.5vw,4.5rem)] leading-[1.1] mb-8" data-id="element-133">
            An AI Meal Planner that knows you better than you do.
          </h2>
          <p className="text-xl text-[var(--fg-muted)] leading-relaxed mb-10" data-id="element-134">
            If we auto-generate a personalized weekly cart based on past orders,
            dietary goals, and budget constraints, users will convert with one
            tap instead of churning.
          </p>

          <div className="space-y-4" data-id="element-135">
            {['Zero-click weekly defaults', 'Macro & calorie target matching', 'Smart variety (no repeating meals too often)', 'One-tap swap for any meal'].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.3 + i * 0.1
          }} className="flex items-center gap-3" data-id="element-136">
                <CheckCircle2 className="text-[var(--accent)]" size={20} data-id="element-137" />
                <span className="text-lg font-medium" data-id="element-138">{feature}</span>
              </motion.div>)}
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.2,
        type: 'spring',
        stiffness: 150,
        damping: 20
      }} className="relative" data-id="element-139">
          {/* Mockup Container */}
          <div className="bg-[var(--surface)] rounded-[3rem] p-6 border-[12px] border-[var(--surface-alt)] shadow-2xl relative z-10 max-w-sm mx-auto aspect-[9/19] flex flex-col overflow-hidden" data-id="element-140">
            {/* Dynamic Island / Notch */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-[var(--surface-alt)] rounded-b-3xl" data-id="element-141" />

            <div className="flex items-center justify-between mt-6 mb-6" data-id="element-142">
              <div className="w-10 h-10 rounded-full bg-[var(--bg)] flex items-center justify-center shadow-sm border border-[var(--surface-alt)]" data-id="element-143">
                <Sparkles className="text-[var(--accent)]" size={18} data-id="element-144" />
              </div>
              <div className="flex gap-2" data-id="element-145">
                <div className="px-3 py-1 bg-[var(--surface-alt)] rounded-full text-[10px] font-bold uppercase tracking-wider text-[var(--fg)]" data-id="element-146">
                  High Protein
                </div>
              </div>
            </div>

            <div className="mb-6" data-id="element-147">
              <h4 className="font-display text-3xl mb-1" data-id="element-148">Your Week</h4>
              <p className="text-sm text-[var(--fg-muted)] font-medium" data-id="element-149">
                7 meals perfectly balanced
              </p>
            </div>

            <div className="space-y-4 flex-1 overflow-hidden relative" data-id="element-150">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[var(--surface)] z-10 pointer-events-none" data-id="element-151" />

              {[{
              day: 'Mon',
              img: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?q=80&w=200&auto=format&fit=crop',
              title: 'Quinoa Bowl'
            }, {
              day: 'Tue',
              img: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?q=80&w=200&auto=format&fit=crop',
              title: 'Salmon Salad'
            }, {
              day: 'Wed',
              img: 'https://images.unsplash.com/photo-1473093295043-cdd812d0e601?q=80&w=200&auto=format&fit=crop',
              title: 'Pesto Pasta'
            }].map((meal, i) => <div key={i} className="bg-[var(--bg)] rounded-2xl border border-[var(--surface-alt)] flex items-center p-2 gap-4 shadow-sm" data-id="element-152">
                  <img src={meal.img} alt={meal.title} className="w-16 h-16 rounded-xl object-cover" data-id="element-153" />
                  <div className="flex-1" data-id="element-154">
                    <div className="text-xs text-[var(--accent)] font-bold uppercase tracking-wider mb-1" data-id="element-155">
                      {meal.day}
                    </div>
                    <div className="font-semibold text-sm" data-id="element-156">{meal.title}</div>
                  </div>
                </div>)}
            </div>

            <div className="mt-4 pt-4 border-t border-[var(--surface-alt)] relative z-20 bg-[var(--surface)]" data-id="element-157">
              <div className="w-full py-4 bg-[var(--fg)] text-[var(--bg)] rounded-2xl font-semibold text-center shadow-lg shadow-[var(--fg)]/20 flex items-center justify-center gap-2" data-id="element-158">
                <Sparkles size={16} data-id="element-159" /> Add to Cart • $84.50
              </div>
            </div>
          </div>

          {/* Decorative background elements instead of blurry blobs */}
          <div className="absolute -top-12 -right-12 w-48 h-48 border border-[var(--accent)]/20 rounded-full -z-10" data-id="element-160" />
          <div className="absolute -top-6 -right-6 w-48 h-48 border border-[var(--accent)]/40 rounded-full -z-10" data-id="element-161" />

          <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-[var(--surface-alt)]/50 rounded-full -z-10" data-id="element-162" />
        </motion.div>
      </div>
    </div>;
}